import type { AppRoute } from '../../routing/route';

export interface ArticleSubsection {
  title: string;
  paragraphs: string[];
  listItems?: string[];
  codeSnippet?: string;
}

export interface ArticleSection {
  title: string;
  paragraphs: string[];
  listItems?: string[];
  subsections?: ArticleSubsection[];
}

export interface ArticleDefinition {
  route: AppRoute;
  id: string;
  title: string;
  subtitle: string;
  lead: string;
  sections: ArticleSection[];
}

export const ARTICLE_DEFINITIONS: Record<'DESIGN' | 'DEVELOPMENT' | 'TESTING' | 'ARCHITECTURE', ArticleDefinition> = {
  DESIGN: {
    route: 'DESIGN',
    id: 'design',
    title: 'ゲームデザインとPresentation設計',
    subtitle: 'Minimal Readable Presentation — Stick Figure Edition',
    lead: '「OFFICE CLASH - 定時なき戦い -」における営業部・開発部のキャラクター特性、棒人間ビジュアル設計、およびCore戦闘状態に同期するバトルシステムの設計方針を解説します。',
    sections: [
      {
        title: '1. 基本コンセプトとキャラクター構成',
        paragraphs: [
          '「OFFICE CLASH - 定時なき戦い -」は、企業の営業部と開発部によるオフィス対戦をテーマにした2D対戦格闘ゲームです。各部門の業務や企業文化をモチーフにした技構成と戦闘スタイルが設定されています。',
        ],
        subsections: [
          {
            title: '営業部（Sales）の戦闘特性',
            paragraphs: [
              '近距離ラッシュと前進打撃を中心とする技構成です。',
            ],
            listItems: [
              '通常技: 名刺ジャブ、クロージングストレート、飛び込み営業',
              '必殺技 (Skill 1): 二枚舌ラッシュ（3連撃の近接コンボ技）',
              '必殺技 (Skill 2): 無理な納期設定（突進技。命中に応じて試合タイマーを直接減算）',
              '超必殺技 (Super): 全社総動員プレゼン（即答コミットで突進する打撃技）',
            ],
          },
          {
            title: '開発部（Developer）の戦闘特性',
            paragraphs: [
              '中距離での空間制御、設置トラップ、および当身カウンターを持つ技構成です。',
            ],
            listItems: [
              '通常技: キーボード殴打、要件定義書スイング、障害対応ドロップ',
              '必殺技 (Skill 1): 仕様変更カウンター（当身技。構え時間中に相手の攻撃を受けて反撃）',
              '必殺技 (Skill 2): スパゲッティコード（設置トラップ。接触した相手にSLOW減速効果を付与）',
              '超必殺技 (Super): Git reset --hard 障害対応（深夜3時の強制ロールバック打撃技）',
            ],
          },
        ],
      },
      {
        title: '2. Minimal Readable Presentation — Stick Figure Edition',
        paragraphs: [
          '本作の描画表現は「Minimal Readable Presentation — Stick Figure Edition」として実装されています。棒人間によるポーズ描画により、攻撃の起動フレーム、ヒットボックス、ヒットストップ、ガード硬直などのCore状態を可視化します。',
        ],
        subsections: [
          {
            title: 'キャラクター別の姿勢とシルエット方針',
            paragraphs: [
              '営業部は warm / forward / open / energetic な前傾姿勢モーション、開発部は cool / grounded / compact / controlled な重心の低い姿勢モーションとして定義されています。',
            ],
          },
        ],
      },
      {
        title: '3. バトルシステム設計',
        paragraphs: [
          '対戦格闘ゲームとしての基本システムと、本作固有の企業モチーフシステムが統合されています。',
        ],
        subsections: [
          {
            title: 'Guard（ガード）と Counter（カウンター）',
            paragraphs: [
              'ガードは専用ガードボタンおよび後ろ入力ガードに対応し、被ダメージを軽減します。',
              '開発部の「仕様変更カウンター」は、構え時間（Counter Window）中に攻撃を受けることで成立し、専用の演出と効果音を伴う反撃を行います。',
            ],
          },
          {
            title: 'Trap & SLOW ステータス',
            paragraphs: [
              '開発部の「スパゲッティコード」で設置されたトラップに接触したキャラクターは、Core側でSLOW状態（移動速度低下）が付与されます。',
            ],
          },
          {
            title: 'TIME_PENALTY（時間減算効果）',
            paragraphs: [
              '営業部の「無理な納期設定」が命中すると、試合残り時間が直接180フレーム（3秒相当）減算されます。',
            ],
          },
          {
            title: 'Super Move（超必殺技）と K.O. 演出',
            paragraphs: [
              'Superゲージ消費により発動する超必殺技は、画面暗転・カットインバナー・技名表示が行われ、通常ヒットフラッシュを抑制して描画されます。',
              '勝敗決着時は最終打撃のヒットストップとともにK.O.バナーが表示され、リザルト画面へ遷移します。',
            ],
          },
        ],
      },
      {
        title: '4. Core Semantics と Presentation Lifetime の同期',
        paragraphs: [
          'Presentation層（描画・演出）は独自のタイマー寿命を持たず、Core（ゲーム論理層）の状態に同期して描画します。',
        ],
        listItems: [
          'TIME_PENALTYは持続ステータスではなく「Hit時のタイマー即時減算（Instant Effect）」として定義され、Presentationは瞬間的な時計・納期アイコンエフェクトのみを描画します。',
          'SLOWはCoreのEFFECT_APPLIEDからEFFECT_EXPIREDの期間のみ描画されます。',
          'トラップ描画はCoreのデバッグボックス（type === \'TRAP\'）の存在に直接同期します。',
        ],
      },
    ],
  },

  DEVELOPMENT: {
    route: 'DEVELOPMENT',
    id: 'development',
    title: '開発工程と実装タイムライン',
    subtitle: 'Deterministic Combat Domain から Web Platform への発展',
    lead: '技術検証（Phase 0）から決定論的戦闘Core、Phaser Presentation統合、そしてWeb配信最適化（Phase 8）に至るOFFICE CLASHの実装履歴を時系列で解説します。',
    sections: [
      {
        title: '1. 開発フェーズのタイムライン',
        paragraphs: [
          'OFFICE CLASHは段階的な仕様策定（FREEZE仕様）と実装・検証フェーズを経て構築されました。',
        ],
        subsections: [
          {
            title: 'Phase 0 〜 Phase 4: コア戦闘基盤の確立',
            paragraphs: [
              'Phase 0にて1280x720固定論理解像度とCanvas描画の技術検証を行い、プロシージャルアトラス生成を確立しました。',
              'Phase 1〜4でPure TypeScriptによる戦闘シミュレーションCoreを実装し、キャラクター固有技、Superゲージ、99秒タイマー、ラウンド進行、勝敗判定（KO、Time Up、Draw）を構築しました。',
            ],
          },
          {
            title: 'Phase 5 〜 Phase 7: UI・Web基盤・表現の刷新',
            paragraphs: [
              'Phase 5でPhaser 4 Scene管理（Title、CharacterSelect、PreBattle、Battle、Result）と音声管理を実装。',
              'Phase 6でWeb設定保存（LocalStorage + メモリフォールバック）とCSPセキュリティヘッダーを導入。',
              'Phase 7にて「Minimal Readable Presentation — Stick Figure Edition」へのビジュアル方針変更を行い、棒人間アニメーションとHUDを実装しました。',
            ],
          },
          {
            title: 'Post-P7 Polish: 演出の洗練とCore同期',
            paragraphs: [
              'タイトル時計配置、2ステップキャラクター選択、リザルト画面incident report演出、Super演出、K.O.演出を実装。',
              'Skill2のTIME_PENALTYをCore論理（即時減算）と同期させ、Presentation独自タイマーを廃止しました。',
            ],
          },
          {
            title: 'Phase 8: Webルーティングと配信アーキテクチャの最適化',
            paragraphs: [
              'Phase 8-0Dでパブリックベースパス（/office-clash/）へ移行。',
              'Phase 8-0G-1でLandingとPlayのURL分離、およびPlay離脱時のPhaserゲームループ一時停止を実装。',
              'フォローアップにおいてphysical play directoryを生成しない構成へ統一し、/playおよび/play/の308リダイレクトを解消しました。',
            ],
          },
        ],
      },
      {
        title: '2. Pure TypeScript Combat Domain',
        paragraphs: [
          '戦闘ロジックはPhaserやDOMに依存しないPure TypeScriptモジュールとして実装されています。',
          '固定60fpsの論理クロックごとに、入力バッファの解析、キャラクター状態遷移、ヒットボックス/ハートボックスの幾何交差判定、ダメージ・ヒットストップ・ノックバックの計算が決定論的に実行されます。',
        ],
      },
      {
        title: '3. Phaser Presentation レイヤーの統合',
        paragraphs: [
          'Phaser 4 は描画・音声再生・入力受付を担当するアダプター層として機能します。',
          'CoreロジックはPhaserやDOMに依存しないPure TypeScriptモジュールとして実装され、Vitestで単体・シミュレーションテストされます。Phaser 4はCoreから出力されるスナップショットデータを受け取り、Stick figureのポーズ更新、エフェクト発生、UI・HUD描画を担当します。',
        ],
      },
      {
        title: '4. Web ライフサイクルと配信最適化',
        paragraphs: [
          'Landing表示中はPhaserをbootせず、Play遷移時にgame instanceを生成します。',
          'Play画面からLandingや技術記事へ遷移した際はPhaserのgame loopと音声を一時停止（Pause）し、Play復帰時に再開（Resume）します。これにより、Play離脱中の論理フレーム進行停止とCanvasインスタンスの維持を自動テストで検証しています。',
        ],
      },
    ],
  },

  TESTING: {
    route: 'TESTING',
    id: 'testing',
    title: '自動テスト戦略と品質保証',
    subtitle: 'Multi-Tier Automated Verification Matrix',
    lead: '決定論的戦闘ロジックのシミュレーション、E2Eシナリオ、視覚的回帰テスト、およびCloudflare配信環境のパリティ検証までを網羅するテスト体系を解説します。',
    sections: [
      {
        title: '1. 多層テストアーキテクチャの概要',
        paragraphs: [
          'OFFICE CLASHでは、コードの健全性と挙動の再現性を検証するために複数のテスト層を導入しています。',
        ],
        listItems: [
          '型検査: tsc --noEmit によるTypeScript型チェック',
          '静的解析: ESLint によるコード規約と静的チェック',
          'ユニット & シミュレーション: Vitest による純粋ロジック・戦闘状態遷移・ダメージ計算の検証',
          '統合テスト: Vitest によるサービス連携・ストレージフォールバックの検証',
          'E2Eテスト: Playwright による実ブラウザでのシーン遷移・ルーティング・ライフサイクルの検証',
          'ビジュアルリグレッションテスト: Playwright による Canvas ピクセル描画の検証',
          'Cloudflare プレビューパリティ検証: Preview 環境と Pages Dev 環境での配信挙動の検証',
          '戦闘診断テスト: 開発用オーバーレイを用いた無敵時間・フレームステップの検証',
        ],
      },
      {
        title: '2. Vitest による決定論的シミュレーションと単体テスト',
        paragraphs: [
          'Pure TypeScriptで記述されたCore domainに対して固定フレーム単位で入力を注入し、ヒット硬直、ガード成立、ダメージ計算、ノックバック距離、ゲージ蓄積、タイマー減算等の状態遷移をVitestで検証します。',
          'DOMやCanvas描画に依存せず、ヘッドレス環境でシミュレーションを実行します。',
        ],
      },
      {
        title: '3. Playwright による E2E & ルーティングライフサイクル検証',
        paragraphs: [
          '実ブラウザ上でTitle → CharacterSelect → PreBattle → Battle → Resultのシーン遷移をテストします。',
          'Landing ↔ Play ↔ 記事間のSPA遷移、直接URLアクセス（Direct Navigation）、ブラウザリロード、ブラウザバック/フォワードを検証しています。',
          'Play離脱時に論理フレームが停止し、Play復帰時にそのフレームから進行が再開することを自動テストで検証しています。',
        ],
      },
      {
        title: '4. ビジュアルリグレッションテスト',
        paragraphs: [
          'Canvas レンダラーにおいて、Stick figure の各ポーズ（ニュートラル、しゃがみ、攻撃、接触、ヒットストップ）の描画結果をゴールデン画像とピクセル比較するテストを実施しています。',
        ],
      },
      {
        title: '5. Cloudflare Preview パリティ検証',
        paragraphs: [
          'Vite Preview（port 4173）と Wrangler Pages Dev（port 4174）の双方で redirect: \'manual\' の下で検証を行います。',
          '各ルート（/office-clash/, /office-clash/play, /office-clash/design, /office-clash/development, /office-clash/testing, /office-clash/architecture）の HTTP 200 返却、Locationヘッダー不在（リダイレクトなし）、X-Robots-Tag の適用状態、およびCSPセキュリティヘッダーの一致を自動検証しています。',
        ],
      },
    ],
  },

  ARCHITECTURE: {
    route: 'ARCHITECTURE',
    id: 'architecture',
    title: 'Web / ゲームアーキテクチャ設計',
    subtitle: 'Core/Presentation 分離と Cloudflare Pages 配信基盤',
    lead: '純粋TypeScript戦闘CoreとPhaser描画アダプターの疎結合設計、SPAクライアントルーティング、およびCloudflare PagesとReverse Proxy Workerの連携基盤を詳解します。',
    sections: [
      {
        title: '1. レイヤードアーキテクチャの構造',
        paragraphs: [
          'OFFICE CLASHのソースコードは責務に応じて明確にレイヤー分割されています。',
        ],
        subsections: [
          {
            title: 'Core Domain',
            paragraphs: [
              '純粋なビジネスロジック層。描画やDOMに依存せず、決定論的な戦闘シミュレーション、状態遷移、衝突判定を実行します。',
            ],
          },
          {
            title: 'Application Layer',
            paragraphs: [
              'サービス初期化、設定ストレージ永続化、エラーハンドリング境界、開発用デバッグAPIを管理します。',
            ],
          },
          {
            title: 'Presentation Layer',
            paragraphs: [
              'Phaser 4 Scene管理、Stick figureアニメーション描画、VFX、HUD、Landingビュー、Articleビューを描画します。',
            ],
          },
          {
            title: 'Routing & SEO Layer',
            paragraphs: [
              '軽量SPAルーター、ルート解決、動的SEOメタデータ（title, description, canonical, robots, OGP）の適用を担います。',
            ],
          },
        ],
      },
      {
        title: '2. Core と Presentation の完全分離',
        paragraphs: [
          'CoreはPhaserやDOMへの参照を持たず、入力と論理フレームを受け取って状態スナップショットを出力します。',
          'PresentationはCoreスナップショットを参照して描画や音声再生を行います。',
        ],
      },
      {
        title: '3. Web ルーティングと SPA Fallback 配信',
        paragraphs: [
          'ビルドスクリプト（scripts/prepare-pages-dist.mjs）は dist/index.html および dist/office-clash/index.html を生成します。',
          'Play画面および各技術記事用の個別 physical HTML directory（dist/play/, dist/design/ 等）は生成せず、Cloudflare Pages の SPA Fallback により単一クライアントアプリケーションへ解決されます。',
          '/play, /design, /development, /testing, /architecture 等のルートが HTTP 200 かつ Location ヘッダーなしで配信されることを preview parity test で検証しています。',
        ],
      },
      {
        title: '4. Cloudflare 配信基盤と Reverse Proxy Worker 連携',
        paragraphs: [
          '公開URLは https://promptlab-games.com/office-clash/ 配下に配置されています。',
          'Reverse Proxy Workerは /office-clash プレフィックスを内部的に除去し、custom origin である https://office-clash-origin.promptlab-games.com へプロキシします。',
          'クライアント側ルーターがブラウザのアドレスバーURLを保持したまま対応するビュー（Landing, Play, 各記事）を表示します。',
        ],
      },
      {
        title: '5. SEO とインデックス制御の分離設計',
        paragraphs: [
          'コンテンツの種別に応じてインデックス制御を分離しています。',
        ],
        subsections: [
          {
            title: 'Landing & 技術記事ルート (/office-clash/, /design, /development, /testing, /architecture)',
            paragraphs: [
              '検索エンジンによるインデックス対象として設定されています（meta robots = index,follow、正式Canonical URL指定）。',
            ],
          },
          {
            title: 'Play ルート (/office-clash/play)',
            paragraphs: [
              '検索結果へのゲーム画面単体の露出を防ぐため、クライアント側DOM meta[name="robots"]="noindex,follow" と _headers による HTTP X-Robots-Tag: noindex, follow の両面で noindex を指定しています。',
            ],
          },
        ],
      },
    ],
  },
};

export const getArticleDefinition = (route: AppRoute): ArticleDefinition | undefined => {
  if (route === 'DESIGN' || route === 'DEVELOPMENT' || route === 'TESTING' || route === 'ARCHITECTURE') {
    return ARTICLE_DEFINITIONS[route];
  }
  return undefined;
};
